<template>
    <div class="pa-2 text-center">
      <v-dialog
        v-model="dialog"
        max-width="600"
      >
        <template v-slot:activator="{ props: activatorProps }">
          <v-btn color="primary" variant="flat" prepend-icon="mdi-plus" v-bind="activatorProps">New Ticket</v-btn>
        </template>

        <v-card
          prepend-icon="mdi-ticket"
          title="Create ticket"
        >
          <v-card-text>
            <v-row dense>
                <v-col cols="12" md="12" sm="12">
                  <v-text-field
                      density="compact"
                      placeholder="Ticket name*"
                      prepend-inner-icon="mdi-information"
                      variant="outlined"
                      v-model="name"
                  ></v-text-field>
                </v-col>

                <v-col v-if="aiEnabled" cols="12" md="12" sm="12" class="d-flex justify-end py-0">
                  <v-btn
                    size="small"
                    variant="tonal"
                    color="primary"
                    prepend-icon="mdi-creation"
                    :loading="aiLoading"
                    :disabled="!name"
                    @click="draftWithAi"
                  >Draft with AI</v-btn>
                </v-col>

                <v-col cols="12" md="12" sm="12">
                  <v-textarea
                      density="compact"
                      placeholder="Description"
                      prepend-inner-icon="mdi-text"
                      variant="outlined"
                      rows="2"
                      v-model="description"
                  ></v-textarea>
                </v-col>

                <v-col cols="12" sm="12">
                  <v-autocomplete
                    prepend-inner-icon="mdi-account"
                    :items="employees"
                    item-title="title"
                    item-value="value"
                    label="Assignee"
                    auto-select-first
                    v-model="assignee"
                    multiple
                  ></v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6">
                <v-autocomplete
                  :items="priorities"
                  item-title="title"
                  item-value="value"
                  label="Priority Level"
                  auto-select-first
                  v-model="priority"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6">
                <v-autocomplete
                  :items="statuses"
                  label="Status"
                  auto-select-first
                  v-model="status"
                ></v-autocomplete>
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field
                  type="date"
                  label="Due date"
                  prepend-inner-icon="mdi-calendar-clock"
                  :min="minDue"
                  :max="maxDue"
                  :hint="sprintStart || sprintEnd ? `Must fall within the sprint window` : 'Cannot be in the past'"
                  persistent-hint
                  v-model="dueDate"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text="Close" variant="plain" @click="dialog = false"></v-btn>
            <v-btn color="primary" text="Save" variant="flat" @click="createTicket()"></v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </template>
<script>
  import axios from 'axios';
  import constant from '@/constant';
  import utils from '@/utils';
  import apiMixin from '@/mixins/apiMixin';
    export default {
        name: "AddTicket",
        mixins: [apiMixin],
        props: {
            projectId: { type: [Number, String], default: null },
            sprintId: { type: [Number, String], default: null },
            sprintStart: { type: String, default: null },
            sprintEnd: { type: String, default: null },
        },
        data() {
            return {
                name: "",
                description: "",
                priority: "medium",
                status: "Todo",
                assignee: [],
                dueDate: null,
                aiEnabled: false,
                aiLoading: false,
                dialog: false,
                constant: constant,
                employees: [],
                priorities: [
                    { title: 'Lowest', value: 'lowest' },
                    { title: 'Low', value: 'low' },
                    { title: 'Medium', value: 'medium' },
                    { title: 'High', value: 'high' },
                    { title: 'Critical', value: 'critical' },
                ],
                statuses: ['Todo', 'In Progress', 'On Hold', 'Cancelled', 'Done'],
                user_data: localStorage.getItem('user_data')
            }
        },
        computed: {
            // Today as YYYY-MM-DD (local) — due dates cannot be in the past.
            today() {
                const d = new Date();
                const off = d.getTimezoneOffset();
                return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
            },
            // Earliest selectable due date: the later of today and the sprint start.
            minDue() {
                if (this.sprintStart && this.sprintStart > this.today) return this.sprintStart;
                return this.today;
            },
            // Latest selectable due date: the sprint end, when a sprint is chosen.
            maxDue() {
                return this.sprintEnd || null;
            },
        },
        methods: {
            // Only show the AI button when the backend has a key configured.
            checkAi() {
                axios.get(utils._api(this.constant.ai_status), this.authHeaders())
                    .then((response) => { this.aiEnabled = !!(response.data && response.data.enabled); })
                    .catch(() => { this.aiEnabled = false; });
            },
            // Ask the backend to draft a description + suggest priority/type
            // from the ticket title, then fill the form for the user to review.
            draftWithAi() {
                if (!this.name) return;
                this.aiLoading = true;
                axios.post(utils._api(this.constant.ai_suggest_ticket), {
                    title: this.name,
                    context: this.description || null,
                }, this.authHeaders())
                    .then((response) => {
                        const d = (response.data && response.data.data) || {};
                        if (d.description) this.description = d.description;
                        if (d.priority) this.priority = d.priority;
                        if (d.type) this.type = d.type;
                        this.$emit('toggle-message', 'AI draft ready — review before saving');
                    })
                    .catch((error) => {
                        const msg = error.response && error.response.data && error.response.data.message
                            ? error.response.data.message
                            : 'AI draft failed';
                        this.$emit('toggle-message', msg);
                    })
                    .finally(() => { this.aiLoading = false; });
            },
            createTicket() {
                // Guard against past or out-of-sprint due dates before hitting the API.
                if (this.dueDate) {
                    if (this.dueDate < this.today) {
                        this.$emit('toggle-message', 'Due date cannot be in the past');
                        return;
                    }
                    if (this.sprintStart && this.dueDate < this.sprintStart) {
                        this.$emit('toggle-message', 'Due date is before the sprint start date');
                        return;
                    }
                    if (this.sprintEnd && this.dueDate > this.sprintEnd) {
                        this.$emit('toggle-message', 'Due date is after the sprint end date');
                        return;
                    }
                }
                const payload = {
                    project_id: this.projectId || null,
                    sprint_id: this.sprintId || null,
                    name: this.name,
                    description: this.description,
                    priority: this.priority,
                    status: this.status,
                    assignees: this.assignee,
                    due_date: this.dueDate || null,
                    reporter: this.user_data ? this.user_data.company_id : null,
                };
                axios.post(utils._api(this.constant.create_ticket), payload, this.authHeaders())
                    .then((response) => {
                        this.dialog = false;
                        this.name = "";
                        this.description = "";
                        this.priority = "medium";
                        this.status = "Todo";
                        this.assignee = [];
                        this.dueDate = null;
                        this.$emit('toggle-message', response.data.message);
                    })
                    .catch(error => {
                        console.error("Error creating ticket:", error);
                        this.$emit('toggle-message', 'Failed to create ticket');
                    });
            },
            getUsers() {
                axios.get(utils._api(this.constant.get_active_users), this.authHeaders())
                    .then((response) => {
                        this.employees = (response.data.data || []).map((employee) => ({
                            title: employee.fullname,
                            value: employee.id
                        }));
                    })
                    .catch(error => console.error("Error fetching users:", error));
            },
        },
        created() {
            const userData = localStorage.getItem('user_data');
            if (userData) {
                this.user_data = JSON.parse(userData);
            } else {
                console.error('User data not found in local storage');
            }
        },
        mounted() {
            this.getUsers();
            this.checkAi();
        },
    }
</script>
